import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { XMarkIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Tip } from '../ui/tip';
import { formatNumberARNoDecimals } from '../../utils/currencyFormatter';

/**
 * Tablero general de reportes: una fila por linea de negocio + transversales.
 * Tendencia 12m (planilla historica en gris, sistema en color), este mes,
 * vs mes anterior, vs año pasado (parcial) y prevision de cierre.
 */

interface SeriePoint { ym: string; source: string; units: number | null; revenue: number | null; }
interface Row {
  key: string; label: string; unitLabel: string;
  serie: SeriePoint[];
  mesActual: { units: number; revenue: number };
  vsAnterior: { units: number | null; revenue: number | null };
  vsAnioPasado: { units: number | null; revenue: number | null; partial?: boolean } | null;
  prevision: { units: number; revenue: number };
  estado: 'up' | 'flat' | 'down';
}

const COLORS: Record<string, string> = { up: '#1baf7a', flat: '#EF9F27', down: '#E24B4A' };
const money = (n: number) => {
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${Math.round(n / 1e3)}k`;
  return `$${formatNumberARNoDecimals(n)}`;
};

function Spark({ serie, color, field = 'revenue' }: { serie: any[]; color: string; field?: string }) {
  const vals = serie.map((p) => (p[field] == null ? null : Number(p[field])));
  const nums = vals.filter((v): v is number => v != null);
  if (nums.length < 2) return <span className="text-xs text-muted-foreground">sin datos</span>;
  const min = Math.min(...nums), max = Math.max(...nums), rg = max - min || 1;
  const w = 88, h = 26, step = w / (vals.length - 1);
  const pts = vals.map((v, i) => (v == null ? null : { x: i * step, y: h - 3 - ((v - min) / rg) * (h - 6), src: serie[i].source }));
  const segments: { d: string; sys: boolean }[] = [];
  let cur: { x: number; y: number }[] = [];
  let curSys = false;
  const flush = () => {
    if (cur.length >= 2) segments.push({ d: 'M' + cur.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' L'), sys: curSys });
    cur = [];
  };
  pts.forEach((p) => {
    if (!p) { flush(); return; }
    const sys = p.src === 'sistema';
    if (cur.length && sys !== curSys) { cur.push(p); flush(); cur = [p]; }
    else cur.push(p);
    curSys = sys;
  });
  flush();
  return (
    <svg width={w} height={h} aria-hidden="true">
      {segments.map((s, i) => (
        <path key={i} d={s.d} fill="none" stroke={s.sys ? color : '#B4B2A9'} strokeWidth={s.sys ? 2 : 1.3} />
      ))}
    </svg>
  );
}

function Delta({ value, invert = false, suffix = '%', muted = false }: { value: number | null; invert?: boolean; suffix?: string; muted?: boolean }) {
  if (value == null) return <span className="text-xs text-muted-foreground">—</span>;
  // 0 = comparo y dio empate; "0%" pelado se leia como "sin dato"
  if (value === 0) return <span className={`text-sm text-muted-foreground ${muted ? 'opacity-60' : ''}`}>≈ igual</span>;
  const good = invert ? value < 0 : value > 0;
  const color = value === 0 ? 'text-muted-foreground' : good ? 'text-green-600' : 'text-red-600';
  const arrow = value > 0 ? '▲' : value < 0 ? '▼' : '';
  return (
    <span className={`text-sm ${muted ? 'opacity-60' : ''} ${color}`}>
      {arrow} {value > 0 ? '+' : ''}{value}{suffix}
    </span>
  );
}

function AgingBar({ aging }: { aging: Record<string, number> }) {
  const total = Object.values(aging).reduce((a, b) => a + b, 0) || 1;
  const segs = [
    { k: 'b0_30', label: '0-30 días', color: '#1baf7a' },
    { k: 'b31_60', label: '31-60', color: '#EF9F27' },
    { k: 'b61_90', label: '61-90', color: '#D85A30' },
    { k: 'b90', label: '+90', color: '#E24B4A' }
  ];
  return (
    <div>
      <div className="flex w-full h-4 rounded overflow-hidden">
        {segs.map((s) => {
          const w = (aging[s.k] / total) * 100;
          return w > 0 ? <div key={s.k} style={{ width: `${w}%`, background: s.color }} /> : null;
        })}
      </div>
      <div className="flex flex-wrap gap-4 mt-2 text-xs text-muted-foreground">
        {segs.map((s) => (
          <span key={s.k}>
            <span className="inline-block w-2.5 h-2.5 rounded-sm mr-1 align-middle" style={{ background: s.color }} />
            {s.label}: {money(aging[s.k] || 0)}
          </span>
        ))}
      </div>
    </div>
  );
}

function CobrarDrill() {
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    axios.get('/api/reports/tablero/cobrar')
      .then((r) => setD(r.data))
      .catch((e) => setErr(e.response?.data?.error || 'Error cargando el detalle'));
  }, []);
  if (err) return <p className="text-sm text-red-600 py-4">{err}</p>;
  if (!d) return <p className="text-sm text-muted-foreground py-4">Cargando detalle...</p>;
  return (
    <div className="space-y-4 pt-1">
      <div>
        <p className="text-sm font-medium mb-2">Antigüedad de la deuda — {money(d.total)} en {d.deudores} cuentas</p>
        <AgingBar aging={d.aging} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-xs text-muted-foreground">
              <th className="pb-2 font-normal">Cliente</th>
              <th className="pb-2 font-normal text-right">Debe</th>
              <th className="pb-2 font-normal text-right">+90 días</th>
              <th className="pb-2 font-normal text-right">Deuda más vieja</th>
              <th className="pb-2 font-normal text-right">Último pago</th>
            </tr>
          </thead>
          <tbody>
            {d.clientes.map((c: any) => (
              <tr key={c.clientId} className="border-t">
                <td className="py-2 pr-2">{c.name}</td>
                <td className="py-2 pr-2 text-right font-medium">{money(c.balance)}</td>
                <td className={`py-2 pr-2 text-right ${c.buckets.b90 > 0 ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                  {c.buckets.b90 > 0 ? money(c.buckets.b90) : '—'}
                </td>
                <td className={`py-2 pr-2 text-right ${c.oldestDays > 90 ? 'text-red-600' : c.oldestDays > 60 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                  {c.oldestDays > 0 ? `${c.oldestDays} días` : '—'}
                </td>
                <td className="py-2 text-right text-muted-foreground">
                  {c.lastPaymentAt ? new Date(c.lastPaymentAt).toLocaleDateString('es-AR') : 'nunca'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const SEV = {
  alta: { color: '#E24B4A', bg: 'bg-red-500/10', label: 'Atención' },
  media: { color: '#EF9F27', bg: 'bg-amber-500/10', label: 'Revisar' },
  info: { color: '#378ADD', bg: 'bg-blue-500/10', label: 'Info' }
} as const;

const PESO_SEV: Record<string, number> = { alta: 0, media: 1, info: 2 };
const ordenarAlertas = (xs: any[]) => [...xs].sort((p, q) => (PESO_SEV[p.severidad] ?? 9) - (PESO_SEV[q.severidad] ?? 9));

export function AlertasBlock({ onNavigate }: { onNavigate?: (tab: string) => void }) {
  const [a, setA] = useState<any>(null);
  const [verDescartadas, setVerDescartadas] = useState(false);
  const navigate = useNavigate();
  // seccion puede ser una pestaña de Reportes ('ventas') o una ruta del sistema ('/diagnostics')
  const goTo = (seccion: string) => {
    if (seccion.startsWith('/')) navigate(seccion);
    else if (onNavigate) onNavigate(seccion);
  };
  const cargar = () => {
    axios.get('/api/reports/tablero/alertas').then((r) => setA(r.data)).catch(() => setA({ alertas: [] }));
  };
  useEffect(() => { cargar(); }, []);

  // "Convertir en tarea": la alerta pasa a Tareas, con vínculo a la pantalla
  // que la resuelve, para asignarla y que no se pierda cuando se descarta.
  const [convertidas, setConvertidas] = useState<Set<string>>(() => new Set());
  const convertirEnTarea = async (al: any) => {
    if (!al.key || convertidas.has(al.key)) return;
    try {
      const vence = new Date();
      vence.setHours(18, 0, 0, 0);
      const ruta = al.seccion ? (String(al.seccion).startsWith('/') ? String(al.seccion) : `/reports?tab=${al.seccion}`) : null;
      await axios.post('/api/tasks', {
        title: al.titulo,
        detail: al.detalle || '',
        priority: al.severidad === 'alta' ? 'high' : 'normal',
        dueAt: vence.toISOString(),
        source: `alert:${al.key}`,
        linkedType: ruta ? 'route' : 'alert',
        linkedId: ruta || al.key,
        linkedLabel: 'Alerta del tablero'
      });
      setConvertidas((prev) => new Set(prev).add(al.key));
      toast.success('Tarea creada', { action: { label: 'Ver tareas', onClick: () => navigate('/tasks') } });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo crear la tarea');
    }
  };

  // Descartar/restaurar actualizan el estado local: nada de volver a pedir el
  // panel entero (se veia como un refresh de la pagina).
  const descartar = async (al: any) => {
    if (!al.key) return;
    setA((prev: any) => (prev ? {
      ...prev,
      alertas: prev.alertas.filter((x: any) => x.key !== al.key),
      descartadas: [{ key: al.key, titulo: al.titulo }, ...(prev.descartadas || [])],
      ocultas: [...(prev.ocultas || []), al]
    } : prev));
    try {
      await axios.post('/api/reports/tablero/alertas/descartar', { key: al.key, titulo: al.titulo });
    } catch {
      // volvio a fallar: la devolvemos a la lista
      setA((prev: any) => (prev ? {
        ...prev,
        alertas: ordenarAlertas([...prev.alertas, al]),
        descartadas: (prev.descartadas || []).filter((d: any) => d.key !== al.key),
        ocultas: (prev.ocultas || []).filter((x: any) => x.key !== al.key)
      } : prev));
    }
  };
  const restaurar = async (key: string) => {
    const original = (a?.ocultas || []).find((x: any) => x.key === key);
    setA((prev: any) => (prev ? {
      ...prev,
      alertas: original ? ordenarAlertas([...prev.alertas, original]) : prev.alertas,
      descartadas: (prev.descartadas || []).filter((d: any) => d.key !== key),
      ocultas: (prev.ocultas || []).filter((x: any) => x.key !== key)
    } : prev));
    try {
      await axios.delete(`/api/reports/tablero/alertas/descartar/${encodeURIComponent(key)}`);
      // si la alerta no estaba en memoria (se descarto en otra sesion) hay que
      // recalcularla en el server para poder mostrarla
      if (!original) cargar();
    } catch { cargar(); }
  };

  if (!a) return null;
  const descartadas: any[] = a.descartadas || [];
  const pieDescartadas = descartadas.length > 0 && (
    <div className="mt-2 pt-2 border-t">
      <button
        type="button"
        onClick={() => setVerDescartadas((v) => !v)}
        className="text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        {verDescartadas ? 'Ocultar' : 'Ver'} descartadas ({descartadas.length})
      </button>
      {verDescartadas && (
        <div className="mt-1.5 space-y-1">
          {descartadas.map((d) => (
            <div key={d.key} className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="min-w-0 truncate line-through">{d.titulo || d.key}</span>
              <button type="button" onClick={() => restaurar(d.key)} className="shrink-0 underline hover:text-foreground">
                restaurar
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (!a.alertas || a.alertas.length === 0) {
    return (
      <Card>
        <CardContent className="py-3">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: '#1baf7a' }} />
            <span className="text-sm text-muted-foreground">Sin alertas — todo en orden por ahora.</span>
          </div>
          {pieDescartadas}
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <h3 className="font-semibold text-sm mb-2.5">Para mirar hoy</h3>
        <div className="space-y-1.5">
          {a.alertas.map((al: any, i: number) => {
            const sev = SEV[al.severidad as keyof typeof SEV] || SEV.info;
            return (
              <div
                key={al.key || i}
                className={`w-full flex items-start gap-2.5 rounded-md px-2.5 py-2 ${sev.bg}`}
              >
                <span className="inline-block w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: sev.color }} />
                <button
                  type="button"
                  onClick={() => al.seccion && goTo(al.seccion)}
                  className="min-w-0 flex-1 text-left hover:opacity-80 transition-opacity"
                >
                  <span className="text-sm font-medium block">{al.titulo}</span>
                  {al.detalle && <span className="text-xs text-muted-foreground block">{al.detalle}{al.seccion ? ' · click para ir' : ''}</span>}
                </button>
                {al.key && (
                  <Tip label={convertidas.has(al.key) ? 'Ya es una tarea' : 'Convertir en tarea'}>
                    <button
                      type="button"
                      disabled={convertidas.has(al.key)}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); convertirEnTarea(al); }}
                      className="shrink-0 mt-0.5 p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-black/5 transition-colors disabled:opacity-50"
                    >
                      <ClipboardDocumentCheckIcon className="w-4 h-4" />
                    </button>
                  </Tip>
                )}
                {al.key && (
                  <Tip label="Descartar esta alerta">
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); descartar(al); }}
                      className="shrink-0 mt-0.5 p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-black/5 transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </Tip>
                )}
              </div>
            );
          })}
        </div>
        {pieDescartadas}
      </CardContent>
    </Card>
  );
}

export default function TableroGeneral({ onNavigate }: { onNavigate?: (tab: string) => void }) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drill, setDrill] = useState<string | null>(null);

  useEffect(() => {
    axios.get('/api/reports/tablero')
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Error cargando el tablero'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-10 text-center text-muted-foreground">Cargando tablero...</div>;
  if (error) return <div className="py-10 text-center text-red-600">{error}</div>;
  if (!data) return null;

  const monthName = new Date(data.month + '-15').toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });

  const lineRows: Row[] = data.lines || [];
  const fact: Row = data.facturacion;

  const renderLineRow = (r: Row, showUnits: boolean, invert = false) => (
    <tr key={r.key} className="border-t">
      <td className="py-2.5 pr-2">
        <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: COLORS[r.estado] }} />
        <span className="font-medium text-sm">{r.label}</span>
      </td>
      <td className="py-2.5 pr-2"><Spark serie={r.serie} color={COLORS[r.estado]} /></td>
      <td className="py-2.5 pr-2 text-sm font-medium whitespace-nowrap">
        {money(r.mesActual.revenue)}
        {showUnits && r.mesActual.units > 0 && (
          <span className="text-xs text-muted-foreground font-normal"> · {formatNumberARNoDecimals(r.mesActual.units)} {r.unitLabel}</span>
        )}
      </td>
      <td className="py-2.5 pr-2">
        <Delta value={r.vsAnterior.revenue} invert={invert} />
        {/* el numero atras del %: mes anterior completo → proyeccion de este mes */}
        {r.serie[10]?.revenue != null && (
          <div className="text-[10px] text-muted-foreground whitespace-nowrap">
            {money(r.serie[10].revenue)} → ~{money(r.prevision.revenue)}
          </div>
        )}
      </td>
      <td className="py-2.5 pr-2">
        <Delta value={r.vsAnioPasado?.revenue ?? null} invert={invert} muted={!!r.vsAnioPasado?.partial} />
        {(r.vsAnioPasado as any)?.baseRevenue != null && (
          <div className="text-[10px] text-muted-foreground whitespace-nowrap">
            {money((r.vsAnioPasado as any).baseRevenue)} → ~{money(r.prevision.revenue)}
          </div>
        )}
      </td>
      <td className="py-2.5 text-sm text-muted-foreground whitespace-nowrap">
        ~{money(r.prevision.revenue)}
        {showUnits && r.prevision.units > 0 && <span className="text-xs"> · {formatNumberARNoDecimals(r.prevision.units)} {r.unitLabel}</span>}
      </td>
    </tr>
  );

  return (
    <div className="space-y-4">
      <AlertasBlock onNavigate={onNavigate} />
      <Card>
        <CardContent className="pt-5">
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="font-semibold">¿Cómo viene cada parte del negocio?</h3>
            <span className="text-xs text-muted-foreground capitalize">{monthName} · al día {data.dayOfMonth}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-xs text-muted-foreground">
                  <th className="pb-2 font-normal">Área</th>
                  <th className="pb-2 font-normal">Tendencia 12m</th>
                  <th className="pb-2 font-normal">Este mes</th>
                  <th className="pb-2 font-normal">vs mes ant.</th>
                  <th className="pb-2 font-normal">vs año pasado *</th>
                  <th className="pb-2 font-normal">Previsión cierre</th>
                </tr>
              </thead>
              <tbody>
                {lineRows.map((r) => renderLineRow(r, true))}
                {renderLineRow(fact, false)}
                {data.gastos && renderLineRow(data.gastos, false, true)}
                {data.resultado && renderLineRow(data.resultado, false)}
                <tr className="border-t">
                  <td className="py-2.5 pr-2">
                    <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: data.margen?.actual != null && data.margen.actual >= 45 ? COLORS.up : COLORS.down }} />
                    <span className="font-medium text-sm">Margen (items con costo)</span>
                  </td>
                  <td className="py-2.5 pr-2"><Spark serie={data.margen.serie} color={COLORS.flat} field="value" /></td>
                  <td className="py-2.5 pr-2 text-sm font-medium">{data.margen.actual != null ? `${data.margen.actual}%` : '—'}</td>
                  <td className="py-2.5 pr-2">
                    <Delta value={data.margen.vsAnterior} suffix=" pts" />
                    {data.margen.serie?.[10]?.value != null && data.margen.actual != null && (
                      <div className="text-[10px] text-muted-foreground whitespace-nowrap">
                        {data.margen.serie[10].value}% → {data.margen.actual}%
                      </div>
                    )}
                  </td>
                  <td className="py-2.5 pr-2 text-xs text-muted-foreground">—</td>
                  <td className="py-2.5 text-sm text-muted-foreground">{data.margen.actual != null ? `${data.margen.actual}%` : '—'}</td>
                </tr>
                <tr className="border-t">
                  <td className="py-2.5 pr-2">
                    <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: (data.reclamos?.actual?.value || 0) > 5 ? COLORS.down : COLORS.up }} />
                    <span className="font-medium text-sm">Reclamos (tasa s/ OTs)</span>
                  </td>
                  <td className="py-2.5 pr-2"><Spark serie={data.reclamos.serie} color={COLORS.flat} field="value" /></td>
                  <td className="py-2.5 pr-2 text-sm font-medium">
                    {data.reclamos.actual?.total > 0 ? `${data.reclamos.actual.reclamos}/${data.reclamos.actual.total} (${data.reclamos.actual.value ?? 0}%)` : 'sin OTs'}
                  </td>
                  <td className="py-2.5 pr-2 text-xs text-muted-foreground">—</td>
                  <td className="py-2.5 pr-2 text-xs text-muted-foreground">—</td>
                  <td className="py-2.5 text-xs text-muted-foreground">—</td>
                </tr>
                <tr
                  className="border-t cursor-pointer hover:bg-accent/40 transition-colors"
                  onClick={() => setDrill(drill === 'cobrar' ? null : 'cobrar')}
                  title=""
                >
                  <td className="py-2.5 pr-2">
                    <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: COLORS.flat }} />
                    <span className="font-medium text-sm">Por cobrar (cta cte)</span>
                    <span className="ml-2 text-xs text-muted-foreground">{drill === 'cobrar' ? '▲ cerrar' : '▼ ver detalle'}</span>
                  </td>
                  <td className="py-2.5 pr-2 text-xs text-muted-foreground">snapshot</td>
                  <td className="py-2.5 pr-2 text-sm font-medium">{money(data.porCobrar.total)}</td>
                  <td className="py-2.5 pr-2 text-xs text-muted-foreground" colSpan={2}>{data.porCobrar.deudores} cuentas con saldo</td>
                  <td className="py-2.5 text-xs text-muted-foreground">—</td>
                </tr>
                {drill === 'cobrar' && (
                  <tr className="border-t bg-accent/20">
                    <td colSpan={6} className="p-3"><CobrarDrill /></td>
                  </tr>
                )}
                <tr className="border-t">
                  <td className="py-2.5 pr-2">
                    <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: COLORS.up }} />
                    <span className="font-medium text-sm">Autos atendidos</span>
                  </td>
                  <td className="py-2.5 pr-2"><Spark serie={data.autos.serie} color={COLORS.up} field="value" /></td>
                  <td className="py-2.5 pr-2 text-sm font-medium">{data.autos.actual}</td>
                  <td className="py-2.5 pr-2">
                    <Delta value={data.autos.vsAnterior ?? null} />
                    {data.autos.serie?.[10]?.value != null && (
                      <div className="text-[10px] text-muted-foreground whitespace-nowrap">
                        {data.autos.serie[10].value} → ~{data.autos.prevision}
                      </div>
                    )}
                  </td>
                  <td className="py-2.5 pr-2 text-xs text-muted-foreground">—</td>
                  <td className="py-2.5 text-xs text-muted-foreground">
                    {data.autos.prevision != null ? `~${data.autos.prevision}` : '—'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-4 mt-3 text-xs text-muted-foreground">
            <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: COLORS.up }} />creciendo</span>
            <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: COLORS.flat }} />estable / atención</span>
            <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: COLORS.down }} />cayendo</span>
            <span className="ml-auto">Gris en la curva = planillas históricas · color = sistema</span>
          </div>
          <p className="text-xs text-muted-foreground mt-2">* {data.coverageNote}</p>
        </CardContent>
      </Card>
    </div>
  );
}
